// src/components/admin/DemoDataManager.jsx

import React, { useState } from 'react';
import { Users, Play, RefreshCw, Trash2 } from 'lucide-react';
import { db } from '../../config/firebase-config';
import { collection, getDocs, writeBatch, doc, query, where, arrayUnion } from 'firebase/firestore';

const DemoDataManager = ({ orgs }) => {
    const [demoOrgId, setDemoOrgId] = useState('');
    const [demoSquadName, setDemoSquadName] = useState('U15 Demo Squad');
    const [isSeeding, setIsSeeding] = useState(false);
    // How many members to seed. Four or fewer lands under the privacy floor, so a
    // suppressed group can be shown live; 60 keeps one write batch under Firestore's limit.
    const [demoCount, setDemoCount] = useState(20);

    // --- SEED CORRELATED DEMO SQUAD (WITH 5-WEEK HISTORY) ---
    const handleSeedDemoData = async () => {
        if (!demoOrgId || !demoSquadName) {
            alert("Please select an organization and enter a squad name.");
            return;
        }
        
        setIsSeeding(true);
        try {
            const batch = writeBatch(db);
            const now = Date.now();
            const dayMs = 24 * 60 * 60 * 1000;
            
            const timepoints = [28, 21, 14, 7, 0];

            const count = Math.max(1, Math.min(60, parseInt(demoCount, 10) || 20));
            for (let i = 1; i <= count; i++) {
                const uid = `demo_user_${now}_${i}`;

                let baseSleep = 85 + (Math.floor(Math.random() * 10) - 5);
                let baseFocus = 80 + (Math.floor(Math.random() * 10) - 5);
                let baseAnxiety = 3 + (Math.floor(Math.random() * 3)); 
                
                let lastStats = null;

                timepoints.forEach((daysAgo, tIdx) => {
                    const assessId = `demo_assess_${now}_${i}_t${tIdx}`;
                    const targetDate = new Date(now - (daysAgo * dayMs)).toISOString();

                    let pointSleep = Math.max(30, baseSleep - (tIdx * 12) + (Math.floor(Math.random() * 8) - 4));
                    let pointAnxiety = Math.min(21, baseAnxiety + (tIdx * 3) + (Math.floor(Math.random() * 2)));
                    let pointFocus = Math.max(35, baseFocus - (tIdx * 9) + (Math.floor(Math.random() * 8) - 4));

                    const stats = {
                        sleep_perf: pointSleep,
                        focus: pointFocus,
                        motivation: Math.max(40, pointSleep + 15), 
                        resilience: 75 + Math.floor(Math.random() * 10), 
                        confidence: 70 + Math.floor(Math.random() * 15),
                        discipline: 80 + Math.floor(Math.random() * 10),
                        awareness: 60 + Math.floor(Math.random() * 15),
                        adaptability: 60 + Math.floor(Math.random() * 15),
                        nutrition: 60 + Math.floor(Math.random() * 20),
                        injury_mgmt: 65 + Math.floor(Math.random() * 15),
                        anxiety: pointAnxiety,
                        cognitive_score: pointFocus,
                        reaction_time: Math.round(250 + ((100 - pointFocus) * 3)) 
                    };

                    lastStats = stats; 

                    const assessRef = doc(db, "assessments", assessId);
                    batch.set(assessRef, {
                        userId: uid,
                        organizationId: demoOrgId,
                        groupId: demoSquadName,
                        isDemo: true,
                        scores: stats,
                        completedAt: targetDate, 
                        status: 'completed'
                    });
                });

                const userRef = doc(db, "users", uid);
                batch.set(userRef, {
                    name: `Demo Member ${i}`,
                    email: `member${i}@${demoSquadName.replace(/\s+/g, '').toLowerCase()}.demo`,
                    role: 'user',
                    organizationId: demoOrgId,
                    groupId: demoSquadName,
                    isDemo: true,
                    createdAt: new Date().toISOString()
                });

                // Scores live in the self-only private_metrics subcollection,
                // mirroring what a real submission writes.
                batch.set(doc(db, "users", uid, "private_metrics", "latest"), {
                    latestStats: lastStats,
                    isDemo: true,
                    updatedAt: new Date().toISOString()
                });
            }

            const orgRef = doc(db, "organizations", demoOrgId);
            batch.set(orgRef, {
                groups: arrayUnion(demoSquadName)
            }, { merge: true });

            await batch.commit();
            alert(`✅ Seeded ${count} members with a 5-week history into ${demoSquadName}.\n\nNote: It takes about 30-60 seconds for the BigQuery pipeline to digest the new data before the AI Analyst can read it.`);

        } catch (error) {
            console.error("Demo Seed Error:", error);
            alert("❌ Error: " + error.message);
        } finally {
            setIsSeeding(false);
        }
    };

    // --- WIPE DEMO DATA ---
    const handleWipeDemoData = async () => {
        if (!window.confirm("⚠️ Delete ALL demo data? This will remove all users and assessments flagged as 'isDemo' across the entire database.")) return;
        
        setIsSeeding(true);
        try {
            const deleteInChunks = async (collectionName, extraRefsFor = null) => {
                const q = query(collection(db, collectionName), where("isDemo", "==", true));
                const snap = await getDocs(q);
                let deletedCount = 0;

                const batches = [];
                let currentBatch = writeBatch(db);
                let operationCount = 0;

                snap.forEach(docSnap => {
                    currentBatch.delete(docSnap.ref);
                    operationCount++;
                    deletedCount++;

                    // Subcollection docs don't cascade — delete them explicitly.
                    if (extraRefsFor) {
                        extraRefsFor(docSnap).forEach(ref => {
                            currentBatch.delete(ref);
                            operationCount++;
                        });
                    }

                    if (operationCount >= 450) {
                        batches.push(currentBatch.commit());
                        currentBatch = writeBatch(db);
                        operationCount = 0;
                    }
                });

                if (operationCount > 0) batches.push(currentBatch.commit());
                await Promise.all(batches);
                return deletedCount;
            };

            const usersDeleted = await deleteInChunks("users",
                (docSnap) => [doc(db, "users", docSnap.id, "private_metrics", "latest")]);
            const assessDeleted = await deleteInChunks("assessments");

            alert(`✅ Wiped ${usersDeleted} Demo Users and ${assessDeleted} Demo Assessments.`);

        } catch (error) {
            console.error("Wipe Error:", error);
            alert("❌ Error: " + error.message);
        } finally {
            setIsSeeding(false);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in">
            {/* Demo Injector Card */}
            <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><Users size={24}/></div>
                    <h2 className="text-xl font-black text-slate-900">Demo Group Seeder</h2>
                </div>
                <p className="text-slate-500 text-sm mb-6">Generates members with correlated scores and a five-week history in one group of an organization, flagged as demo data. The warehouse picks them up within a minute and the console switches from the modeled example to live figures. </p>
                
                <div className="space-y-4 mb-8">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Target Organization</label>
                        <select 
                            className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                            value={demoOrgId} 
                            onChange={(e) => setDemoOrgId(e.target.value)}
                        >
                            <option value="" disabled>-- Choose Org --</option>
                            {orgs.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Group name (a team, unit or department)</label>
                        <input 
                            type="text"
                            className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                            value={demoSquadName}
                            onChange={(e) => setDemoSquadName(e.target.value)}
                            placeholder="e.g. U15 Demo Squad, Frontline Patrol"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">How many members</label>
                        <input
                            type="number" min="1" max="60"
                            className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 font-medium focus:ring-2 focus:ring-blue-500 outline-none"
                            value={demoCount}
                            onChange={(e) => setDemoCount(e.target.value)}
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Four or fewer stays under the privacy floor, so the console shows that group as suppressed. Each member gets five weekly check-ins.</p>
                    </div>
                </div>

                <button 
                    onClick={handleSeedDemoData}
                    disabled={isSeeding || !demoOrgId || !demoSquadName}
                    className="w-full mt-auto py-4 bg-slate-900 text-white rounded-xl font-bold flex justify-center items-center gap-2 hover:bg-black transition-colors disabled:opacity-50"
                >
                    {isSeeding ? <RefreshCw className="animate-spin" size={20}/> : <Play size={20} className="fill-white"/>}
                    {isSeeding ? "Seeding…" : `Seed ${demoCount || 20} members`}
                </button>
            </div>

            {/* Demo Wipe Card */}
            <div className="bg-red-50 p-6 md:p-8 rounded-3xl border border-red-100 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-red-500 mb-6 shadow-sm">
                    <Trash2 size={32}/>
                </div>
                <h3 className="text-xl font-black text-red-900 mb-2">Wipe Demo Environment</h3>
                <p className="text-red-700/80 text-sm mb-8 max-w-xs">Instantly deletes all dummy users and assessments flagged as 'isDemo'. Keeps your production data clean.</p>
                
                <button 
                    onClick={handleWipeDemoData}
                    disabled={isSeeding}
                    className="px-8 py-4 bg-red-600 text-white rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-red-500/30 hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                    <Trash2 size={20} /> Wipe All Demo Data
                </button>
            </div>
        </div>
    );
};

export default DemoDataManager;